import { DateTime } from 'luxon';
import {
  Subscription,
  catchError,
  distinctUntilChanged,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { API } from '@models/API';

import { CoreMetricsApi } from '@services/API/trusts/lender/core-metrics/core-metrics.service';
import { InsigthsAPI } from '@services/API/trusts/operations/insight/insight.service';
import { OperationsState } from '@services/state/operation-state/operation.state';

/**
 * A facility and the cut-off its figures are reported at, as one value.
 * Always read the two together -- see `CutOffDateState.facilitySelection`.
 */
export interface FacilitySelection {
  id: string;
  periodISO?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CutOffDateState {
  private CoreMetricsApi = inject(CoreMetricsApi);
  private insightsAPI = inject(InsigthsAPI);
  private operationsState = inject(OperationsState);

  public cutOffs = signal<API.Trusts.Operations.Insigths.CutOffs.Response>([]);
  public lastUpdated = computed(() => {
    if (this.cutOffs().length === 0) {
      return '-';
    } else {
      const latestCutOff = this.cutOffs().reduce((latest, current) => {
        const currentDate = this.parseDate(current.date);
        const bestDate = this.parseDate(latest.date);
        return currentDate > bestDate ? current : latest;
      });
      return latestCutOff?.date ?? '-';
    }
  });

  // Only set by `setFacilitiesCutOffDatesByApi` (Cash Flow's own consumer,
  // LEN-2105) -- `setOverviewCutOffDatesByApi` doesn't touch it, since
  // nothing outside Cash Flow reads it yet.
  public cutOffsLoaded = signal(false);

  public currentPeriod = signal<string | undefined>(undefined);
  public currentPeriodISO = computed(() => {
    if (!this.currentPeriod()) {
      return undefined;
    }

    return DateTime.fromFormat(this.currentPeriod()!, 'dd/MM/yyyy').toFormat(
      'yyyy-MM-dd'
    );
  });

  public currentPeriod$ = toObservable(this.currentPeriod);
  public currentPeriodISO$ = toObservable(this.currentPeriodISO);

  /**
   * Operation `cutOffs`/`currentPeriod` were loaded for, or `undefined` when
   * they belong to no facility -- not loaded yet, or the lender-wide Overview
   * set them (`setOverviewCutOffDatesByApi`).
   */
  private facilityOwnerId = signal<string | undefined>(undefined);

  /**
   * The facility's id and cut-off as a single value, so a consumer can never
   * read one without the other.
   *
   * This service is root-provided but its data is per-facility, and nothing
   * invalidates it when a facility is left: `setFacilitiesCutOffDatesByApi`
   * watches `activeId` through a `toObservable` created in the *calling
   * component's* injection context, so that watcher dies with the page.
   * Leaving a facility for the list therefore kept the previous cut-off
   * alive here, and the next facility's page -- resolving `activeId` on its
   * own, ahead of this service's refetch -- paired its own id with it and
   * fired `insights` (404), `insights-resume` and `charts/v2` (503) for a
   * `reported_at` that facility never reported (LEN-2158).
   *
   * Pairing the two in a `computed` is what makes that unrepresentable:
   * `activeId` moving on drops the period in the same read, with no effect
   * flush, subscription or component lifetime in between. Consumers must
   * take the pair from here rather than combining `activeId` with
   * `currentPeriodISO$` themselves.
   */
  public facilitySelection = computed<FacilitySelection>(() => {
    const id = this.operationsState.activeId();
    const isOwned = !!id && this.facilityOwnerId() === id;

    return { id, periodISO: isOwned ? this.currentPeriodISO() : undefined };
  });

  public facilitySelection$ = toObservable(this.facilitySelection).pipe(
    // `facilitySelection` rebuilds its object on every dependency read, and
    // consumers turn each emission into a request batch.
    distinctUntilChanged((a, b) => a.id === b.id && a.periodISO === b.periodISO)
  );

  setOverviewCutOffDatesByApi(trustId: string) {
    // Set this to undefined, so we don't keep the previous value
    // for any requests. As the lender could change, but we wont have
    // the lender's cutOffs in time. Is better to just simply send an
    // undefined, as we don'r know the cutOffs yet.
    this.cutOffs.set([]);
    this.currentPeriod.set(undefined);
    this.facilityOwnerId.set(undefined);

    return this.CoreMetricsApi.getCutOffDates(trustId).pipe(
      tap(res => {
        const cutOffs = res.map(date => ({ date }));
        this.cutOffs.set(cutOffs);
        this.currentPeriod.set(cutOffs[0]?.date);
      }),
      // The Overview subscribes to this inside a `switchMap` over
      // `lenderState.selected$` with no error handler
      // (`general-overview.component.ts:68`), so an error reaching it ends that
      // subscription: picking another lender would never ask for its cut-offs
      // again for the life of the page. The reset above already left the
      // selector empty, and the error interceptor still raises the global
      // error, so absorbing it here costs no visibility (MR !1319 review S2).
      catchError(() => of<string[]>([]))
    );
  }

  // Insights, Concentrations and Cash Flow each call this from their own
  // constructor, and `toObservable(activeId)` never completes on its own --
  // without tearing down the previous one, every page visit added another
  // live subscription racing the rest to write `cutOffs`/`cutOffsLoaded`,
  // making them non-deterministic after a few visits (MR !1199 review, S4).
  private cutOffsSubscription?: Subscription;

  setFacilitiesCutOffDatesByApi(loaderKey: boolean | string = true) {
    this.cutOffsSubscription?.unsubscribe();
    this.cutOffsSubscription = toObservable(this.operationsState.activeId)
      .pipe(
        switchMap(id => {
          // Reset per facility, not just once for this call -- Angular
          // reuses `CashFlowLayout` (and this method's single invocation
          // from its constructor) across a route change that only swaps
          // `:operationId`, so `activeId` re-fires this `switchMap` on its
          // own without the method itself running again. Resetting only
          // above would leave a previous facility's `cutOffsLoaded=true` +
          // `cutOffs=[]` in place while this one is still loading, flashing
          // its empty state (MR !1199 review, S1).
          this.cutOffs.set([]);
          this.currentPeriod.set(undefined);
          this.cutOffsLoaded.set(false);
          this.facilityOwnerId.set(undefined);

          return this.insightsAPI
            .cutOffs(id, DateTime.now().toFormat('yyyy-MM-dd'), loaderKey)
            .pipe(
              tap(res => {
                this.cutOffs.set(res);
                this.currentPeriod.set(res[0]?.date);
                this.facilityOwnerId.set(id);
                this.cutOffsLoaded.set(true);
              })
            );
        })
      )
      .subscribe();
  }

  parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  }
}
